import { useCallback, useEffect, useRef, useState } from 'react';
import { FiHeart, FiMessageSquare, FiSearch, FiX } from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';
import MessageModal from '../../components/dashboard/MessageModal.js';
import { buildApiUrl } from '../../config/api';
import { useAuth } from '../../context/AuthContext.js';
import { countries as countryOptions } from '../../data/selectOptions';
import { fetchJson, formatFetchError } from '../../utils/fetchJson';
import { getProfilePhotoUrl } from '../../utils/profileUtils';

const INITIAL_FILTERS = {
  gender: '',
  country: '',
  city: '',
  ageFrom: 18,
  ageTo: 40
};

const Profiles = () => {
  const [profiles, setProfiles] = useState(() => {
    try {
      const cached = localStorage.getItem('cached_browse_profiles');
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });
  const [initialLoading, setInitialLoading] = useState(() => !localStorage.getItem('cached_browse_profiles'));

  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const [likedProfilesMap, setLikedProfilesMap] = useState(new Set());
  const [viewingImage, setViewingImage] = useState(null);
  const [messageModal, setMessageModal] = useState({
    isOpen: false,
    recipient: null
  });
  const [showFilterModal, setShowFilterModal] = useState(false);

  const filterFetchTimeoutRef = useRef(null);

  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const enforceOppositeGender = currentUser?.gender === 'male' || currentUser?.gender === 'female';

  const buildQueryString = useCallback((nextFilters) => {
    const params = new URLSearchParams();

    if (!enforceOppositeGender && nextFilters.gender) params.set('gender', nextFilters.gender);
    if (nextFilters.country) {
      const countryFromValue = countryOptions.find((c) => c.value === nextFilters.country);
      const countryFromLabel = countryOptions.find((c) => c.label === nextFilters.country);
      const countryCode = countryFromValue?.value || countryFromLabel?.value || nextFilters.country;
      const countryLabel = countryFromValue?.label || countryFromLabel?.label;

      params.set('country', countryCode);
      if (countryLabel) params.set('countryLabel', countryLabel);
    }
    if (nextFilters.city) params.set('city', nextFilters.city);

    if (nextFilters.ageFrom !== '' && nextFilters.ageFrom !== null && nextFilters.ageFrom !== undefined) {
      params.set('ageFrom', nextFilters.ageFrom);
    }
    if (nextFilters.ageTo !== '' && nextFilters.ageTo !== null && nextFilters.ageTo !== undefined) {
      params.set('ageTo', nextFilters.ageTo);
    }

    return params.toString();
  }, [enforceOppositeGender]);

  const fetchProfiles = useCallback(async (nextFilters) => {
    // Only set fetching UI if we don't have data, or for filtering interactions
    setIsFetching(true);
    setError('');

    try {
      const token = localStorage.getItem('token');

      if (!token) {
        setProfiles([]);
        setError('Please login to browse profiles');
        return;
      }

      const queryString = buildQueryString(nextFilters);
      const url = buildApiUrl(`/api/dashboard/browse-profiles${queryString ? `?${queryString}` : ''}`);

      const { response, data } = await fetchJson(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        if (response.status === 401) {
          navigate('/login');
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (data?.success) {
        const fetched = Array.isArray(data?.data?.profiles) ? data.data.profiles : [];
        setProfiles(fetched);
        localStorage.setItem('cached_browse_profiles', JSON.stringify(fetched));

        // Sync local liked state with server source of truth
        setLikedProfilesMap(prev => {
          const next = new Set(prev);
          fetched.forEach(p => {
            // Use server's isLiked status if available
            if (p.isLiked === true) {
              next.add(p.id || p._id);
            } else if (p.isLiked === false) {
              // Only remove if explicitly false and we have the ID (avoids clearing on partial updates if that were to happen)
              next.delete(p.id || p._id);
            }
          });
          localStorage.setItem('likedProfilesMap', JSON.stringify([...next]));
          return next;
        });
      } else {
        setProfiles([]);
        setError(data?.message || 'Failed to fetch profiles');
      }
    } catch (err) {
      console.error('Error fetching profiles:', err);
      setProfiles([]);
      setError(formatFetchError(err, 'Failed to fetch profiles. Please try again.'));
    } finally {
      setIsFetching(false);
      setInitialLoading(false);
    }
  }, [buildQueryString, navigate]);

  useEffect(() => {
    fetchProfiles(INITIAL_FILTERS);

    const cachedLikedMap = localStorage.getItem('likedProfilesMap');
    if (cachedLikedMap) {
      try {
        const likedIds = JSON.parse(cachedLikedMap);
        setLikedProfilesMap(new Set(likedIds));
      } catch (e) {
        console.log('Error parsing cached liked profiles:', e);
      }
    }

    return () => {
      if (filterFetchTimeoutRef.current) {
        clearTimeout(filterFetchTimeoutRef.current);
      }
    };
  }, [fetchProfiles]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    setFilters(prev => {
      const next = {
        ...prev,
        [name]: value
      };

      if (filterFetchTimeoutRef.current) {
        clearTimeout(filterFetchTimeoutRef.current);
      }

      // Debounce to avoid firing a request on every keystroke.
      filterFetchTimeoutRef.current = setTimeout(() => {
        fetchProfiles(next);
      }, 300);

      return next;
    });
  };

  const handleApplyFilters = () => {
    console.log('Applying filters:', filters);
    if (filterFetchTimeoutRef.current) {
      clearTimeout(filterFetchTimeoutRef.current);
      filterFetchTimeoutRef.current = null;
    }
    fetchProfiles(filters);
  };

  const handleResetFilters = () => {
    const nextFilters = INITIAL_FILTERS;
    if (filterFetchTimeoutRef.current) {
      clearTimeout(filterFetchTimeoutRef.current);
      filterFetchTimeoutRef.current = null;
    }
    setFilters(nextFilters);
    fetchProfiles(nextFilters);
  };

  const handleLikeProfile = async (profile) => {
    const profileId = profile?.id || profile?._id;
    if (!profileId) return;

    const isCurrentlyLiked = likedProfilesMap.has(profileId);

    try {
      const newLikedMap = new Set(likedProfilesMap);
      if (isCurrentlyLiked) {
        newLikedMap.delete(profileId);
      } else {
        newLikedMap.add(profileId);
      }
      setLikedProfilesMap(newLikedMap);
      localStorage.setItem('likedProfilesMap', JSON.stringify([...newLikedMap]));

      const token = localStorage.getItem('token');
      const { response, data } = await fetchJson(buildApiUrl(`/api/user-profiles/${profileId}/like`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok || !data?.success) {
        setLikedProfilesMap(likedProfilesMap);
        localStorage.setItem('likedProfilesMap', JSON.stringify([...likedProfilesMap]));
        setError(data?.message || 'Failed to update like status');
      }
    } catch (err) {
      setLikedProfilesMap(likedProfilesMap);
      localStorage.setItem('likedProfilesMap', JSON.stringify([...likedProfilesMap]));
      console.error('Error liking profile:', err);
      setError(formatFetchError(err, 'Failed to update like status. Please try again.'));
    }
  };

  const handleOpenMessage = (profile) => {
    setMessageModal({
      isOpen: true,
      recipient: profile
    });
  };

  const handleCloseMessageModal = () => {
    setMessageModal({
      isOpen: false,
      recipient: null
    });
  };



  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const birthDate = new Date(dateOfBirth);
    if (Number.isNaN(birthDate.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const normalizeAge = (value) => {
    if (value === null || value === undefined) return null;
    const num = typeof value === 'string' ? parseInt(value, 10) : value;
    if (!Number.isFinite(num) || num <= 0) return null;
    return num;
  };

  const getProfileAge = (profile) => {
    const directAge = normalizeAge(profile?.age);
    if (directAge) return directAge;
    return normalizeAge(calculateAge(profile?.dateOfBirth || profile?.dob));
  };

  const formatLocation = (country, city) => {
    if (country) return country;
    if (city) return city;
    return 'Not specified';
  };

  const getMatchPercent = (profile) => {
    const raw = profile?.compatibilityScore;
    const value = typeof raw === 'string' ? parseFloat(raw) : raw;
    if (Number.isFinite(value) && value > 0) {
      return Math.max(0, Math.min(100, Math.round(value)));
    }
    return 80;
  };

  const handleViewProfile = (profile) => {
    const userId = profile?.id || profile?._id;
    if (!userId) return;
    navigate(`/dashboard/user-profile/${userId}`, { state: { from: location.pathname + location.search } });
  };

  if (initialLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-romantic-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Mobile Filters Button - Only show on mobile */}
      <div className="md:hidden mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-romantic font-bold text-gray-900">Profiles</h1>
          <p className="text-sm text-gray-600">Find your perfect match</p>
        </div>
        <button
          onClick={() => setShowFilterModal(true)}
          className="flex items-center space-x-2 bg-romantic-500 text-white px-4 py-2.5 rounded-lg shadow-md hover:bg-romantic-600 transition-all"
        >
          <span className="font-semibold text-sm">Filters</span>
          <FiSearch size={18} />
        </button>
      </div>

      {/* Desktop Filters - Hidden on mobile */}
      <div className="hidden md:block bg-white rounded-2xl p-6 shadow-lg border border-gray-100 mb-8">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-xl font-semibold text-gray-900">Filters</h2>
          <button
            onClick={handleResetFilters}
            className="text-romantic-600 hover:text-romantic-700 font-medium text-xs"
          >
            Reset Filters
          </button>
        </div>

        <div className="flex flex-nowrap items-end gap-2 overflow-x-auto pb-1">
          {!enforceOppositeGender && (
            <div className="flex-1 min-w-[100px]">
              <label className="block text-[10px] font-semibold text-gray-700 mb-1">Gender</label>
              <select
                name="gender"
                value={filters.gender}
                onChange={handleFilterChange}
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-romantic-500 focus:border-transparent"
              >
                <option value="">Any Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
          )}

          <div className="flex-[1.5] min-w-[120px]">
            <label className="block text-[10px] font-semibold text-gray-700 mb-1">Country</label>
            <select
              name="country"
              value={filters.country}
              onChange={handleFilterChange}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-romantic-500 focus:border-transparent"
            >
              <option value="">Any Country</option>
              {countryOptions.map((country) => (
                <option key={country.value} value={country.value}>
                  {country.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1 min-w-[100px]">
            <label className="block text-[10px] font-semibold text-gray-700 mb-1">City</label>
            <input
              type="text"
              name="city"
              value={filters.city}
              onChange={handleFilterChange}
              placeholder="Any city"
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-romantic-500 focus:border-transparent"
            />
          </div>

          <div className="flex-none w-[140px]">
            <label className="block text-[10px] font-semibold text-gray-700 mb-1">Age Range</label>
            <div className="flex items-center space-x-1">
              <input
                type="number"
                name="ageFrom"
                value={filters.ageFrom}
                onChange={handleFilterChange}
                placeholder="18"
                min="18"
                max="80"
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-romantic-500 focus:border-transparent"
              />
              <span className="text-gray-400 text-[10px]">to</span>
              <input
                type="number"
                name="ageTo"
                value={filters.ageTo}
                onChange={handleFilterChange}
                placeholder="40"
                min="18"
                max="80"
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-romantic-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex space-x-2 pb-[1px]">
            <button
              onClick={handleApplyFilters}
              className="bg-romantic-500 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-romantic-600 transition-all shadow-sm active:scale-95 whitespace-nowrap"
            >
              Apply Filters
            </button>
            <button
              onClick={handleResetFilters}
              className="border border-gray-300 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-gray-50 transition-all active:scale-95 whitespace-nowrap"
            >
              Clear All
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Filter Modal */}
      {showFilterModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end md:hidden">
          <div className="bg-white w-full rounded-t-3xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col animate-slide-up">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Filters</h2>
              <button
                onClick={() => setShowFilterModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <FiX size={24} className="text-gray-600" />
              </button>
            </div>

            {/* Modal Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Country */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Country</label>
                <select
                  name="country"
                  value={filters.country}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-romantic-500 focus:border-transparent text-base"
                >
                  <option value="">Any Country</option>
                  {countryOptions.map((country) => (
                    <option key={country.value} value={country.value}>
                      {country.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* City */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">City</label>
                <input
                  type="text"
                  name="city"
                  value={filters.city}
                  onChange={handleFilterChange}
                  placeholder="Any City"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-romantic-500 focus:border-transparent text-base"
                />
              </div>

              {/* Age Range */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Age Range</label>
                <div className="space-y-3">
                  <select
                    name="ageFrom"
                    value={filters.ageFrom}
                    onChange={handleFilterChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-romantic-500 focus:border-transparent text-base"
                  >
                    {Array.from({ length: 63 }, (_, i) => i + 18).map(age => (
                      <option key={age} value={age}>{age}</option>
                    ))}
                  </select>
                  <div className="text-center text-sm text-gray-500 font-medium">to</div>
                  <select
                    name="ageTo"
                    value={filters.ageTo}
                    onChange={handleFilterChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-romantic-500 focus:border-transparent text-base"
                  >
                    {Array.from({ length: 63 }, (_, i) => i + 18).map(age => (
                      <option key={age} value={age}>{age}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Modal Footer - Action Buttons */}
            <div className="p-4 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => {
                  handleResetFilters();
                  setShowFilterModal(false);
                }}
                className="flex-1 border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-all"
              >
                Clear All
              </button>
              <button
                onClick={() => {
                  handleApplyFilters();
                  setShowFilterModal(false);
                }}
                className="flex-1 bg-romantic-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-romantic-600 transition-all shadow-md"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-2 rounded-lg mb-6 text-sm">
          {error}
        </div>
      )}

      {isFetching && (
        <div className="flex justify-center items-center mb-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-romantic-500"></div>
        </div>
      )}

      {/* Profiles Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {profiles.map((profile) => {
          const profileId = profile.id || profile._id;
          const age = getProfileAge(profile);
          const matchPercent = getMatchPercent(profile);
          const isLiked = likedProfilesMap.has(profileId);

          return (
            <div
              key={profileId}
              className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden flex flex-col hover:shadow-xl transition-shadow"
            >
              <div className="relative h-[320px] w-full">
                <img
                  src={getProfilePhotoUrl(profile)}
                  alt={`${profile.firstName} ${profile.lastName}`}
                  className="h-full w-full object-cover cursor-pointer"
                  onClick={() => setViewingImage(getProfilePhotoUrl(profile))}
                />
                <div className="absolute top-3 left-3 bg-white/90 text-xs px-3 py-1 rounded-full text-gray-700 shadow">
                  {formatLocation(profile.country, profile.city)}
                </div>
                <div className="absolute top-3 right-3 bg-romantic-500 text-xs px-3 py-1 rounded-full text-white shadow">
                  {matchPercent}% Match
                </div>
              </div>

              <div className="p-5 flex-1 flex flex-col">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3
                      className="text-lg font-semibold text-gray-900 cursor-pointer"
                      onClick={() => handleViewProfile(profile)}
                    >
                      {profile.firstName} {profile.lastName}{age ? `, ${age}` : ''}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {profile.profession || 'Not specified'}
                    </p>
                  </div>
                </div>



                <div className="mt-3 mb-4">
                  <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                    <div
                      className="h-2.5 rounded-full bg-gradient-to-r from-romantic-500 to-primary-500"
                      style={{ width: `${matchPercent}%` }}
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Compatibility Alignment
                  </p>
                </div>

                <div className="mt-auto flex items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => handleOpenMessage(profile)}
                    className="flex-1 inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold rounded-lg text-white bg-gradient-to-r from-romantic-500 to-primary-500 hover:from-romantic-600 hover:to-primary-600 shadow-md hover:shadow-lg transition-all"
                  >
                    <FiMessageSquare size={16} className="mr-2" />
                    Message
                  </button>
                  <button
                    type="button"
                    onClick={() => handleViewProfile(profile)}
                    className="flex-1 inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold rounded-lg border border-romantic-200 text-romantic-600 bg-rose-50 hover:bg-rose-100 transition-colors"
                  >
                    View Profile
                  </button>
                  <button
                    type="button"
                    onClick={() => handleLikeProfile(profile)}
                    className={`p-2.5 rounded-lg transition-all ${isLiked
                      ? 'bg-romantic-500 text-white shadow-md'
                      : 'bg-gray-100 text-gray-500 hover:bg-romantic-100 hover:text-romantic-500'
                      }`}
                    title={isLiked ? 'Unlike' : 'Like'}
                  >
                    <FiHeart className={isLiked ? 'fill-current' : ''} size={18} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* No Profiles Message */}
      {!isFetching && profiles.length === 0 && (
        <div className="text-center py-12">
          <div className="flex justify-center mb-4">
            <FiSearch size={56} className="text-gray-300" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No profiles found</h3>
          <p className="text-gray-600">Try adjusting your filters to see more matches</p>
        </div>
      )}

      <MessageModal
        isOpen={messageModal.isOpen}
        onClose={handleCloseMessageModal}
        recipient={messageModal.recipient}
        currentUser={currentUser}
        onSendMessage={() => { }}
      />

      {/* Full Screen Image Viewer */}
      {viewingImage && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center p-4" onClick={() => setViewingImage(null)}>
          <button
            onClick={() => setViewingImage(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 p-2 rounded-full bg-black/50 transition-colors"
          >
            <FiX size={32} />
          </button>
          <img
            src={viewingImage}
            alt="Full size profile"
            className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default Profiles;